/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo } from "react";
import { UseRequestRecommendationsProps } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestRecommendations/requestRecommendations.model";
import { getCloudinaryOptimizedUrl } from "@companion-core/shared/app/Utils/cloudinary";
import {
  APIAddress,
  APIProposal,
  APIRecommendation,
} from "@companion-core/shared/app/Interfaces/API";
import { trackPageRequestRecommendations } from "@companion-core/shared/app/tracking/requests";

type RecommendationImage = { order: number; url: string; description_alt?: string };

export const useRequestRecommendations = ({
  requestData,
  proposalsData,
}: UseRequestRecommendationsProps) => {
  const { id: requestId, family, sub_family, vendor } = requestData;
  const proposal = useMemo(
    () =>
      (proposalsData?.data?.results as APIProposal[])?.find(
        (proposition) => proposition.status === "generated"
      ),
    [proposalsData]
  );

  const proposalId = useMemo(() => proposal?.id as string, [proposal]);
  const recommendations = useMemo(() => proposal?.recommendations ?? [], [proposal]);
  const introduction = useMemo(() => proposal?.introduction as string, [proposal]);
  const conclusion = useMemo(() => proposal?.conclusion as string, [proposal]);
  const getRecommendationMedia = useCallback((recommendation: APIRecommendation) => {
    const imageUrl = (recommendation?.images as RecommendationImage[] | undefined)?.find(
      (image) => image.order === 1
    )?.url;
    return imageUrl ? getCloudinaryOptimizedUrl(imageUrl, { contentType: "logo" }) : undefined;
  }, []);
  const getRecommendationMediaAlt = useCallback((recommendation: APIRecommendation) => {
    return (recommendation?.images as RecommendationImage[] | undefined)?.find(
      (image) => image.order === 1
    )?.description_alt;
  }, []);
  const getRecommendationSubtitle = useCallback(
    (recommendation: APIRecommendation) => recommendation.summary,
    []
  );
  const getRecommendationBudget = useCallback(
    (recommendation: APIRecommendation) => recommendation.price as string | undefined,
    []
  );
  const getRecommendationBeginningAddress = useCallback(
    (recommendation: APIRecommendation) => recommendation.beginning_address as APIAddress,
    []
  );
  const getRecommendationEndingAddress = useCallback(
    (recommendation: APIRecommendation) => recommendation.ending_address as APIAddress,
    []
  );

  useEffect(() => {
    trackPageRequestRecommendations({
      proposalId,
      requestId,
      family,
      subFamily: sub_family,
      vendorId: vendor?.vendor_id,
    });
  }, []);

  return {
    proposalId,
    recommendations,
    introduction,
    conclusion,
    getRecommendationMedia,
    getRecommendationMediaAlt,
    getRecommendationSubtitle,
    getRecommendationBudget,
    getRecommendationBeginningAddress,
    getRecommendationEndingAddress,
  };
};
